import { useState, useRef, useEffect } from 'react';
import Link from '@/lib/compat/link';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLayout } from './layout-context';
import { useTranslations } from '@/lib/i18n/client';
import { NotificationBell } from './notification-bell';
import { GlobalCommandMenu } from './global-command-menu';

interface MobileHeaderProps {
  title: string;
  subtitle?: string;
  showWarehouseSelector?: boolean;
  warehouseName?: string;
  onNotificationClick?: () => void;
  onWarehouseClick?: () => void;
  rightContent?: React.ReactNode;
  
  // Back navigation
  showBackButton?: boolean;
  backHref?: string;
  onBackClick?: () => void;
  showNotificationBell?: boolean; // default: true
  showGlobalSearch?: boolean; // default: true
}

export function MobileHeader({
  title,
  subtitle,
  showWarehouseSelector,
  warehouseName,
  onNotificationClick,
  onWarehouseClick,
  rightContent,
  showBackButton = false,
  backHref,
  onBackClick,
  showNotificationBell = true,
  showGlobalSearch = true,
}: MobileHeaderProps) {
  const { subNavItems, pathname } = useLayout();
  const t = useTranslations();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Sub-nav dropdown is only for list pages (no back button).
  // Detail/form pages with showBackButton should NOT show the dropdown.
  const hasSubNav = subNavItems.length > 1 && !showBackButton;

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownOpen]);

  // Close on route change
  useEffect(() => {
    setDropdownOpen(false);
  }, [pathname]);

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    }
  };

  return (
    <header ref={headerRef} className="sticky top-0 z-40 bg-background border-b border-border lg:hidden">
      {/* Main header row */}
      <div className="flex items-center justify-between px-4 h-14">
        {/* Back button */}
        {showBackButton && (
          <div className="flex items-center gap-2 shrink-0 ltr:mr-3 rtl:ml-3">
            {backHref ? (
              <Link
                href={backHref}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </Link>
            ) : (
              <button
                onClick={handleBackClick}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            )}
          </div>
        )}

        <div className="flex-1 min-w-0">
          {hasSubNav ? (
            <button
              onClick={() => setDropdownOpen((prev) => !prev)}
              className="flex items-center gap-1.5"
            >
              <h1 className="truncate text-xl font-bold text-foreground">{title}</h1>
              <ChevronDown
                className={cn(
                  'w-4 h-4 text-muted-foreground transition-transform shrink-0 mt-0.5',
                  dropdownOpen && 'rotate-180'
                )}
              />
            </button>
          ) : (
            <>
              <h1 className="truncate text-xl font-bold text-foreground">{title}</h1>
              {subtitle && (
                <p className="text-sm text-muted-foreground">{subtitle}</p>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {rightContent}
          {showGlobalSearch && <GlobalCommandMenu triggerVariant="icon" />}
          {showNotificationBell && (
            <NotificationBell />
          )}
        </div>
      </div>

      {/* Sub-nav dropdown — overlay, doesn't push content */}
      {hasSubNav && dropdownOpen && (
        <>
          <div className="fixed inset-0 top-14 z-30" onClick={() => setDropdownOpen(false)} />
          <div className="absolute left-0 right-0 top-14 z-40 border-t border-border bg-background  max-h-[60vh] overflow-y-auto">
            {subNavItems.map((item) => {
              const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 text-sm transition-colors',
                    isActive
                      ? 'bg-primary/5 text-primary font-medium'
                      : 'text-foreground hover:bg-muted'
                  )}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  <span>{t(item.labelKey)}</span>
                </Link>
              );
            })}
          </div>
        </>
      )}

      {/* Warehouse selector row */}
      {showWarehouseSelector && (
        <div className="px-4 pb-3">
          <button
            onClick={onWarehouseClick}
            className={cn(
              'flex items-center gap-2 w-full px-4 py-2.5 rounded-lg',
              'bg-muted border border-border',
              'text-sm text-foreground font-medium',
              'hover:bg-accent transition-colors'
            )}
          >
            <span className="flex-1 text-start truncate">
              {warehouseName || t('stockLocations.selectLocation')}
            </span>
            <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
          </button>
        </div>
      )}
    </header>
  );
}
